import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { CreativeApproachDetailCard } from './CreativeApproachDetailCard';
import type { ElementCategoryRow } from './data/api';
import { ElementCategoryCard, ElementCategoryCardSkeleton } from './ElementCategoryCard';

const CATEGORY_SKELETON_COUNT = 5;

export interface CreativeApproachDetailCardInput {
  icon: ReactNode;
  title: string;
  creatives: number | null;
  sectionTitle: string;
  sectionDescription: string;
  categoryTitle: string;
  bullets: string[];
  accentColor: string;
  elements: { feature: string; tag: string }[];
  thumbnailUrl: string | null;
  previewUrl: string | null;
  duration: number | null;
  creativeType: string | null;
  leadingKpi: number | null;
  leadingKpiLabel: string;
}

interface CreativeApproachCardsSectionProps {
  categories: ElementCategoryRow[];
  categoriesLoading: boolean;
  selectedCategory: string | null;
  onSelectCategory: (category: string) => void;
  totalCreatives: number | null;
  showDetailCards: boolean;
  detailCardsLoading: boolean;
  onToggleDetailCards: () => void;
  getDetailCardProps: (category: string) => CreativeApproachDetailCardInput | null;
  collapsedPreview?: boolean;
  showMargin?: boolean;
}

export function CreativeApproachCardsSection({
  categories,
  categoriesLoading,
  selectedCategory,
  onSelectCategory,
  totalCreatives,
  showDetailCards,
  detailCardsLoading,
  onToggleDetailCards,
  getDetailCardProps,
  collapsedPreview = false,
  showMargin = true,
}: CreativeApproachCardsSectionProps): ReactNode {
  const detailCards = categories
    .map((row) => ({ category: row.category, detail: getDetailCardProps(row.category) }))
    .filter(
      (entry): entry is { category: string; detail: CreativeApproachDetailCardInput } =>
        entry.detail !== null,
    );

  return (
    <>
      {collapsedPreview ? null : (
        <>
          <Typography sx={{ color: 'text.secondary', fontSize: 14, mt: 3 }}>
            Elements are grouped into categories that help you understand the type of element:
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14, mt: 2 }}>
            We analyzed your{' '}
            <Typography component="strong" sx={{ color: 'text.primary', fontWeight: 700 }}>
              {totalCreatives?.toLocaleString() ?? '—'} creatives{' '}
            </Typography>
            and categorized them into {categories.length} groups based on their creative approach.
          </Typography>
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          gap: 5,
          justifyContent: 'center',
          mt: showMargin ? 6 : 0,
        }}
      >
        {categoriesLoading
          ? Array.from(
              { length: CATEGORY_SKELETON_COUNT },
              (_, index) => `category-skeleton-${index}`,
            ).map((id) => <ElementCategoryCardSkeleton key={id} />)
          : categories.map((row) => (
              <ElementCategoryCard
                key={row.category}
                category={row}
                selected={selectedCategory === row.category}
                onSelect={() => onSelectCategory(row.category)}
              />
            ))}
      </Box>
      {collapsedPreview ? null : (
        <ButtonBase onClick={onToggleDetailCards} sx={{ display: 'block', mt: 3, mx: 'auto' }}>
          <Typography sx={{ color: 'primary.main', fontSize: 14, fontWeight: 500 }}>
            {showDetailCards ? 'Hide' : 'See'} How YOUR Elements and Tags Fit Each Category
          </Typography>
        </ButtonBase>
      )}
      {showDetailCards && !collapsedPreview ? (
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            mt: 4,
          }}
        >
          {detailCardsLoading
            ? Array.from(
                { length: categories.length || CATEGORY_SKELETON_COUNT },
                (_, index) => `detail-skeleton-${index}`,
              ).map((id, index, skeletons) => {
                const isLastOfOdd = skeletons.length % 2 === 1 && index === skeletons.length - 1;
                return (
                  <Box
                    key={id}
                    sx={
                      isLastOfOdd
                        ? { display: 'flex', gridColumn: '1 / -1', justifyContent: 'center' }
                        : undefined
                    }
                  >
                    <Skeleton
                      height={230}
                      sx={{ borderRadius: 3.5, width: isLastOfOdd ? 'calc(50% - 7.5px)' : '100%' }}
                      variant="rectangular"
                    />
                  </Box>
                );
              })
            : detailCards.map(({ category, detail }, index) => {
                const isLastOfOdd =
                  detailCards.length % 2 === 1 && index === detailCards.length - 1;
                if (!isLastOfOdd) return <CreativeApproachDetailCard key={category} {...detail} />;
                return (
                  <Box
                    key={category}
                    sx={{ display: 'flex', gridColumn: '1 / -1', justifyContent: 'center' }}
                  >
                    <Box sx={{ width: 'calc(50% - 7.5px)' }}>
                      <CreativeApproachDetailCard {...detail} />
                    </Box>
                  </Box>
                );
              })}
        </Box>
      ) : null}
    </>
  );
}
